import React, { useState, useEffect, useRef } from 'react'
import { useDebounce } from '../../hooks/useDebounce'
import { createLogger } from '../../utils/logger'
import { getAnswerValue } from '../../utils/answerUtils'

const logger = createLogger('StudentProducedResponse')

export default function StudentProducedResponse({
  textAnswer,
  onTextAnswerChange,
  onTextAnswerBlur,
  onTextAnswerFocus,
  onTextAnswerSubmit,
  onDebouncedChange,
  readOnly = false,
  showCorrectAnswer = false,
  correctAnswer = null
}) {
  const [localValue, setLocalValue] = useState(textAnswer || '')
  const debouncedValue = useDebounce(localValue, 1500) // 1.5 second delay
  const lastSavedValueRef = useRef(textAnswer || '') // Track what was last saved to backend

  // Update local value when prop changes (e.g., hydration from server)
  useEffect(() => {
    setLocalValue(textAnswer || '')
    // Also update the last saved value when hydrating from server
    lastSavedValueRef.current = textAnswer || ''
  }, [textAnswer])

  // Call debounced change handler when debounced value changes
  // Compare to lastSavedValue instead of textAnswer to avoid race condition
  useEffect(() => {
    if (debouncedValue !== lastSavedValueRef.current && onDebouncedChange) {
      logger.debug('🔄 Debounced save triggered:', {
        debouncedValue,
        lastSaved: lastSavedValueRef.current
      })
      onDebouncedChange(debouncedValue)
      lastSavedValueRef.current = debouncedValue // Update after saving
    }
  }, [debouncedValue, onDebouncedChange])

  const hasTextAnswer = localValue && typeof localValue === 'string' && localValue.trim()

  // Normalize the acceptable answers for comparison. Schema-v2 stores correct_answer as an object
  // ({ value } / { values: [...] }); getAnswerValue() turns any shape into a display string, and we
  // accept a match against any single acceptable value (not the joined string).
  const acceptableAnswers = React.useMemo(() => {
    if (correctAnswer === null || correctAnswer === undefined) return []
    const raw = Array.isArray(correctAnswer)
      ? correctAnswer
      : Array.isArray(correctAnswer?.values)
        ? correctAnswer.values
        : [correctAnswer]
    return raw.map((a) => getAnswerValue(a).trim().toLowerCase()).filter(Boolean)
  }, [correctAnswer])

  const isAnswerCorrect = React.useMemo(() => {
    if (!showCorrectAnswer || acceptableAnswers.length === 0) return null
    const userAnswer = (localValue || '').trim().toLowerCase()
    return acceptableAnswers.includes(userAnswer)
  }, [showCorrectAnswer, acceptableAnswers, localValue])

  const handleChange = (e) => {
    // Don't allow changes in read-only mode
    if (readOnly) return
    const newValue = e.target.value
    setLocalValue(newValue)
    // Call immediate change handler for UI updates
    if (onTextAnswerChange) {
      onTextAnswerChange(newValue)
    }
  }

  const handleBlur = () => {
    // Don't process blur in read-only mode
    if (readOnly) return
    // Flush any pending debounced changes on blur
    if (onDebouncedChange && localValue !== lastSavedValueRef.current) {
      logger.debug('🔄 Blur save triggered:', { localValue, lastSaved: lastSavedValueRef.current })
      onDebouncedChange(localValue)
      lastSavedValueRef.current = localValue // Update after saving
    }
    if (onTextAnswerBlur) {
      onTextAnswerBlur()
    }
  }

  // Determine border color based on read-only mode and correctness
  const getBorderClass = () => {
    if (readOnly && showCorrectAnswer) {
      if (isAnswerCorrect === true) {
        return 'border-2 border-green-500 bg-green-50 dark:bg-green-900/20'
      } else if (isAnswerCorrect === false) {
        return 'border-2 border-red-500 bg-red-50 dark:bg-red-900/20'
      }
    }
    if (hasTextAnswer) {
      return 'border-green-300 dark:border-green-700 bg-green-50 dark:bg-green-900/20'
    }
    return 'border-gray-200 dark:border-gray-700'
  }

  const getInputBorderClass = () => {
    if (readOnly && showCorrectAnswer) {
      if (isAnswerCorrect === true) {
        return 'border-green-400 dark:border-green-600 bg-green-50 dark:bg-green-900/30'
      } else if (isAnswerCorrect === false) {
        return 'border-red-400 dark:border-red-600 bg-red-50 dark:bg-red-900/30'
      }
    }
    if (hasTextAnswer) {
      return 'border-green-400 dark:border-green-600 focus:ring-2 focus:ring-green-500 dark:focus:ring-green-600 focus:border-green-500 dark:focus:border-green-600'
    }
    return 'border-gray-300 dark:border-gray-600 focus:ring-2 focus:ring-primary-500 dark:focus:ring-primary-600 focus:border-primary-500 dark:focus:border-primary-600'
  }

  return (
    <div className="space-y-3">
      <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">Your Answer</h3>
      <div
        className={`bg-white dark:bg-gray-800 border rounded-lg p-4 transition-colors ${getBorderClass()}`}
      >
        <input
          type="text"
          value={localValue}
          onChange={handleChange}
          onBlur={handleBlur}
          onFocus={readOnly ? undefined : onTextAnswerFocus}
          onKeyDown={(e) => {
            if (readOnly) return
            if (e.key === 'Enter' && onTextAnswerSubmit) {
              e.preventDefault()
              onTextAnswerSubmit()
            }
          }}
          placeholder={readOnly ? 'No answer provided' : 'Enter your answer here...'}
          readOnly={readOnly}
          disabled={readOnly}
          className={`w-full px-4 py-3 border rounded-lg text-lg transition-colors text-gray-900 dark:text-gray-100 placeholder-gray-400 dark:placeholder-gray-500 ${getInputBorderClass()} ${
            readOnly
              ? 'bg-gray-50 dark:bg-gray-800 cursor-not-allowed'
              : 'bg-white dark:bg-gray-900'
          }`}
        />
        <div className="flex items-center justify-between mt-2">
          {readOnly ? (
            // Read-only mode: show correct/incorrect status
            <div className="flex items-center space-x-4">
              {showCorrectAnswer && isAnswerCorrect !== null && (
                <div
                  className={`flex items-center text-sm font-medium ${isAnswerCorrect ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'}`}
                >
                  {isAnswerCorrect ? (
                    <>
                      <svg className="w-4 h-4 mr-1" fill="currentColor" viewBox="0 0 20 20">
                        <path
                          fillRule="evenodd"
                          d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                          clipRule="evenodd"
                        />
                      </svg>
                      Correct
                    </>
                  ) : (
                    <>
                      <svg className="w-4 h-4 mr-1" fill="currentColor" viewBox="0 0 20 20">
                        <path
                          fillRule="evenodd"
                          d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"
                          clipRule="evenodd"
                        />
                      </svg>
                      Incorrect
                    </>
                  )}
                </div>
              )}
              {showCorrectAnswer && correctAnswer && !isAnswerCorrect && (
                <div className="text-sm text-gray-600 dark:text-gray-400">
                  Correct answer:{' '}
                  <span className="font-medium text-green-600 dark:text-green-400">
                    {getAnswerValue(correctAnswer)}
                  </span>
                </div>
              )}
            </div>
          ) : (
            // Edit mode: original UI
            <p className="text-sm text-gray-500 dark:text-gray-400">
              Type your answer. For math, use numbers (e.g., 403, 12.5, -8).
            </p>
          )}
          <div className="flex items-center space-x-2">
            {!readOnly && hasTextAnswer && (
              <div className="flex items-center text-green-600 dark:text-green-400 text-sm">
                <svg className="w-4 h-4 mr-1" fill="currentColor" viewBox="0 0 20 20">
                  <path
                    fillRule="evenodd"
                    d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                    clipRule="evenodd"
                  />
                </svg>
                Answer ready
              </div>
            )}
            {!readOnly && (
              <>
                <span className="text-xs text-gray-400 dark:text-gray-500">
                  {hasTextAnswer ? localValue.length : 0} characters
                </span>
                {localValue !== debouncedValue && (
                  <span className="text-xs text-primary-500 dark:text-primary-400">Saving...</span>
                )}
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}
